/**
 * ClaimOfferModal — fires from the "Claim This Offer →" CTA on institution
 * `special_offer` Flow cards. Reuses the existing conversations + messages
 * pipeline (POST /api/posts/:id/claim-offer) so the student stays inside
 * Unexa and the institution gets a real chat thread + notification.
 *
 * Zero credits are charged on either side. Auth required — the API returns
 * 403 for guests; we redirect them to /auth first.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '@/App';
import { useIntent, checkIntentAction } from '@/components/IntentSheet';

export function ClaimOfferModal({ post, onClose }) {
  const { user } = useAuth();
  const { openIntentSheet } = useIntent();
  const navigate = useNavigate();
  const instName = post.inst_name || post.user_name || 'this institution';
  const [phone, setPhone] = useState(user?.phone || '');
  const [message, setMessage] = useState(
    `I'd like to claim the exclusive offer posted by ${instName} on Unexa.`,
  );
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!user) { navigate('/auth'); return; }
    if (sending) return;
    // Intent gate — claim_offer's threshold is 0, so we only open the sheet
    // if any of the high-value fields are missing. Keeps marketplace liquid.
    const check = await checkIntentAction('claim_offer');
    if (!check.allowed) {
      onClose?.();
      openIntentSheet({ action: 'claim_offer', onComplete: () => _send() });
      return;
    }
    _send();
  };

  const _send = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/posts/${post.post_id}/claim-offer`, {
        post_id: post.post_id,
        phone: phone.trim() || null,
        message: message.trim() || null,
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="claim-offer-modal"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-black text-slate-800">Claim This Offer</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Send your details to {instName} to claim this exclusive offer.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
            data-testid="claim-offer-close"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {done ? (
          /* Success */
          <div className="p-6 text-center" data-testid="claim-offer-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 mx-auto flex items-center justify-center mb-3">
              <Send className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-1">Request sent</h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              Your request has been sent to {instName}. They will contact you shortly.
            </p>
            <button
              onClick={onClose}
              className="mt-5 w-full py-2.5 rounded-xl bg-slate-100 text-slate-600 text-sm font-bold"
              data-testid="claim-offer-done"
            >
              Done
            </button>
          </div>
        ) : (
          /* Form */
          <div className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Field
                label="Name"
                value={user?.name || ''}
                readOnly
                testId="claim-offer-name"
              />
              <Field
                label="Email"
                value={user?.email || ''}
                readOnly
                testId="claim-offer-email"
              />
            </div>
            <Field
              label="Phone (optional)"
              value={phone}
              onChange={setPhone}
              placeholder="e.g. +61 4xx xxx xxx"
              testId="claim-offer-phone"
            />
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">
                Message
              </label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm leading-relaxed focus:outline-none focus:border-blue-400 resize-none"
                data-testid="claim-offer-message"
              />
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <button
                onClick={submit}
                disabled={sending}
                className="w-full py-3 rounded-xl bg-[#1A7A6E] hover:bg-[#16685D] text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] transition-all"
                data-testid="claim-offer-submit"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? 'Sending…' : 'Send Request →'}
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 text-xs font-bold text-slate-500 hover:text-slate-700"
                data-testid="claim-offer-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, readOnly, placeholder, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">
        {label}
      </label>
      <input
        value={value}
        onChange={onChange ? e => onChange(e.target.value) : undefined}
        readOnly={readOnly}
        placeholder={placeholder}
        className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-blue-400 transition-all ${
          readOnly ? 'bg-slate-50 border-slate-100 text-slate-500' : 'bg-white border-slate-200 text-slate-700'
        }`}
        data-testid={testId}
      />
    </div>
  );
}
